import React, { useState, useMemo, useEffect } from 'react';
import { AcademicCapIcon, BookOpenIcon, TrophyIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { AcademicCapIcon as AcademicCapIconSolid } from '@heroicons/react/24/solid';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line, Area, AreaChart } from 'recharts';

interface DiscipleshipStatusSectionProps {
  totalStudents?: number;
  activePrograms?: number;
  completedCourses?: number;
  graduationRate?: number;
}

// 색상 상수
const DISCIPLESHIP_COLORS = ['#059669', '#DC2626', '#1D4ED8', '#7C3AED'];
const COURSE_COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444'];
const LEVEL_COLORS = ['#06B6D4', '#8B5CF6', '#EC4899', '#F97316'];

export const DiscipleshipStatusSection: React.FC<DiscipleshipStatusSectionProps> = ({
  totalStudents = 156,
  activePrograms = 8,
  completedCourses = 342,
  graduationRate = 78
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'programs' | 'progress' | 'trends'>('overview');
  const [animatedValues, setAnimatedValues] = useState({
    totalStudents: 0,
    activePrograms: 0,
    completedCourses: 0,
    graduationRate: 0
  });

  // 애니메이션 효과
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedValues({
        totalStudents,
        activePrograms,
        completedCourses,
        graduationRate
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [totalStudents, activePrograms, completedCourses, graduationRate]);

  // 양육 프로그램 데이터
  const programData = useMemo(() => [
    {
      name: '새신자 과정',
      students: 45,
      completion: 89,
      color: '#10B981',
      duration: '12주',
      description: '새로 등록한 신자를 위한 기초 과정'
    },
    {
      name: '제자훈련',
      students: 38,
      completion: 76,
      color: '#3B82F6',
      duration: '24주',
      description: '깊이 있는 신앙 성장을 위한 과정'
    },
    {
      name: '리더십 과정',
      students: 24,
      completion: 82,
      color: '#F59E0B',
      duration: '16주',
      description: '교회 리더십 개발 프로그램'
    },
    {
      name: '전문 사역 과정',
      students: 18,
      completion: 94,
      color: '#EF4444',
      duration: '20주',
      description: '특별 사역을 위한 전문 훈련'
    }
  ], []);

  // 레벨별 분포 데이터
  const levelData = useMemo(() => [
    { level: '초급', students: 62, percentage: 40 },
    { level: '중급', students: 47, percentage: 30 },
    { level: '고급', students: 31, percentage: 20 },
    { level: '리더급', students: 16, percentage: 10 }
  ], []);

  // 월별 진도 데이터
  const progressTrendData = useMemo(() => [
    { month: '1월', 신규등록: 12, 수료: 8, 진행중: 145 },
    { month: '2월', 신규등록: 15, 수료: 11, 진행중: 149 },
    { month: '3월', 신규등록: 18, 수료: 14, 진행중: 153 },
    { month: '4월', 신규등록: 14, 수료: 16, 진행중: 151 },
    { month: '5월', 신규등록: 16, 수료: 12, 진행중: 155 },
    { month: '6월', 신규등록: 13, 수료: 15, 진행중: totalStudents }
  ], [totalStudents]);

  // 성취도 계산
  const averageCompletion = Math.round(programData.reduce((acc, program) => acc + program.completion, 0) / programData.length);
  
  return (
    <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl p-6 mb-8 shadow-lg border border-green-200 dark:border-green-800">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl">
            <AcademicCapIconSolid className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">양육관리 현황</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">제자 양육 프로그램과 성장 과정 관리</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            averageCompletion >= 80 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
            averageCompletion >= 70 ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' :
            'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
          }`}>
            <span className={`w-2 h-2 rounded-full mr-2 inline-block ${
              averageCompletion >= 80 ? 'bg-green-500' :
              averageCompletion >= 70 ? 'bg-blue-500' : 'bg-yellow-500'
            } animate-pulse`}></span>
            평균 수료율 {averageCompletion}%
          </div>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="flex flex-wrap gap-1 mb-6 bg-white dark:bg-gray-800 p-1 rounded-xl">
        <button
          onClick={() => setActiveTab('overview')}
          className={`flex-1 min-w-[100px] py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'overview'
              ? 'bg-green-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          전체 현황
        </button>
        <button
          onClick={() => setActiveTab('programs')}
          className={`flex-1 min-w-[100px] py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'programs'
              ? 'bg-green-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          프로그램별
        </button>
        <button
          onClick={() => setActiveTab('progress')}
          className={`flex-1 min-w-[100px] py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'progress'
              ? 'bg-green-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          진도 관리
        </button>
        <button
          onClick={() => setActiveTab('trends')}
          className={`flex-1 min-w-[100px] py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'trends'
              ? 'bg-green-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          성과 분석
        </button>
      </div>

      {/* 콘텐츠 영역 */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* 핵심 지표 카드 */}
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">총 수강생</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{animatedValues.totalStudents}명</p>
              </div>
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <BookOpenIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">활성 프로그램</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{animatedValues.activePrograms}개</p>
              </div>
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <AcademicCapIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">수료율</p>
                <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{animatedValues.graduationRate}%</p>
              </div>
              <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <TrophyIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
          </div>
        </div>

        {/* 주요 내용 */}
        <div className="lg:col-span-3">
          {activeTab === 'overview' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">양육 프로그램 전체 현황</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">프로그램별 수강생</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie 
                        data={programData} 
                        dataKey="students" 
                        nameKey="name" 
                        cx="50%" 
                        cy="50%" 
                        outerRadius={70} 
                        labelLine={false} 
                        label={{ fontSize: 12 }}
                      >
                        {programData.map((entry, index) => (
                          <Cell key={`cell-program-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [`${value}명`, name]} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">레벨별 분포</h4>
                  <div className="space-y-3">
                    {levelData.map((item, index) => (
                      <div key={item.level} className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: LEVEL_COLORS[index] }}></div>
                          <span className="text-sm text-gray-600 dark:text-gray-300">{item.level}</span>
                        </div>
                        <span className="font-medium text-gray-800 dark:text-white">
                          {item.students}명 ({item.percentage}%)
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <p className="text-sm text-green-700 dark:text-green-300">
                      <strong>전체 수료 과정:</strong> {completedCourses}건
                    </p>
                    <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                      올해 총 수료한 과정 수
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'programs' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">프로그램별 상세 현황</h3>
              <div className="space-y-4">
                {programData.map((program, index) => (
                  <div key={program.name} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: program.color }}></div>
                        <h4 className="text-md font-medium text-gray-700 dark:text-gray-300">{program.name}</h4>
                        <span className="text-xs bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded-full text-gray-600 dark:text-gray-300">
                          {program.duration}
                        </span>
                      </div>
                      <span className="text-lg font-bold text-gray-900 dark:text-white">{program.students}명</span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{program.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                          <span>수료율</span>
                          <span>{program.completion}%</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full transition-all duration-1000" 
                            style={{ 
                              backgroundColor: program.color,
                              width: `${program.completion}%`
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'progress' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">양육 진도 관리</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-sm text-blue-600 dark:text-blue-400 mb-1">진행 중</p>
                  <p className="text-2xl font-bold text-blue-800 dark:text-blue-200">{totalStudents - Math.floor(totalStudents * 0.1)}명</p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                  <p className="text-sm text-green-600 dark:text-green-400 mb-1">이번 달 수료</p>
                  <p className="text-2xl font-bold text-green-800 dark:text-green-200">15명</p>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <p className="text-sm text-yellow-600 dark:text-yellow-400 mb-1">지연 중</p>
                  <p className="text-2xl font-bold text-yellow-800 dark:text-yellow-200">8명</p>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
                  <p className="text-sm text-purple-600 dark:text-purple-400 mb-1">신규 등록</p>
                  <p className="text-2xl font-bold text-purple-800 dark:text-purple-200">13명</p>
                </div>
              </div>
              
              <div>
                <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">진도 요약</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 dark:text-gray-400">• 평균 진도율: {averageCompletion}%</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">• 예상 수료 시기: 3-4개월 후</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">• 전체 진행 상황: 양호</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 dark:text-gray-400">• 개별 상담 필요: 12명</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">• 우수 수강생: 23명</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">• 종합 평가: 우수</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'trends' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">양육 성과 분석</h3>
              
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">6개월 진행 추이</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={progressTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="신규등록" stroke="#10B981" strokeWidth={2} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="수료" stroke="#3B82F6" strokeWidth={2} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="진행중" stroke="#F59E0B" strokeWidth={2} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600 dark:text-green-400 font-medium">성장률</p>
                      <p className="text-xl font-bold text-green-800 dark:text-green-200">+12%</p>
                    </div>
                    <ChartBarIcon className="h-8 w-8 text-green-500" />
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">품질 지수</p>
                      <p className="text-xl font-bold text-blue-800 dark:text-blue-200">우수</p>
                    </div>
                    <TrophyIcon className="h-8 w-8 text-blue-500" />
                  </div>
                </div>

                <div className="bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 p-4 rounded-lg border border-emerald-200 dark:border-emerald-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">전망</p>
                      <p className="text-xl font-bold text-emerald-800 dark:text-emerald-200">매우 긍정적</p>
                    </div>
                    <BookOpenIcon className="h-8 w-8 text-emerald-500" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};